import axios from 'axios';
import { DriverRef, PartnerCompanyRef, SharedColumnsResponse, TruckRef, deserializeShared } from 'src/entities/Course.entity';
import { RouteSourceEntity, RouteSourceResponse, deserializeRouteSource } from 'src/entities/Timetable.entity';

export type SharedColumns = { defaultTruck: TruckRef | null; defaultDriver: DriverRef | null; partnerCompany: PartnerCompanyRef | null };

/**
 * コースマスタ API のエラー（docs/api_v5_master_courses.md・api_v5_master_timetables.md「エラー」「ダイヤとして保存」）。
 * 画面が分岐する code だけ構造を持ち、それ以外は other（文言をそのまま出す）
 */
export type CourseMasterError =
  | { code: 'trip_group_mismatch'; message: string; self: SharedColumns; target: { id: number; code: string; tripNo: number; name: string | null } & SharedColumns }
  | { code: 'first_trip_not_in_timetables'; message: string; timetables: { id: number; name: string }[] }
  | { code: 'truck_shared_by_trip_groups'; message: string; timetableId: number; truckId: number; courses: { id: number; code: string; tripNo: number }[] }
  | { code: 'linked_trips_exist'; message: string }
  | { code: 'route_source_required'; message: string; courseId: number; sources: RouteSourceEntity[] }
  | { code: 'first_trip_missing'; message: string; trips: { courseId: number; code: string; tripNo: number; firstTripCourse: { id: number; code: string } }[] }
  | { code: 'place_invalid'; message: string; places: { name: string | null; address: string | null; errors: string[] }[] }
  | { code: 'other'; message: string; status: number | null };

type Body = { error?: unknown; code?: unknown } & Record<string, unknown>;
type TripRef = { id: number; code: string; trip_no: number; name: string | null };

export function parseCourseMasterError(error: unknown, fallback = '保存できませんでした'): CourseMasterError {
  if (!axios.isAxiosError(error)) return { code: 'other', message: fallback, status: null };
  const body = (error.response?.data ?? {}) as Body;
  const message = typeof body.error === 'string' ? body.error : fallback;
  switch (body.code) {
    case 'trip_group_mismatch': {
      const b = body as unknown as { self: SharedColumnsResponse; target: TripRef & SharedColumnsResponse };
      return { code: 'trip_group_mismatch', message, self: deserializeShared(b.self),
        target: { id: b.target.id, code: b.target.code, tripNo: b.target.trip_no, name: b.target.name, ...deserializeShared(b.target) } };
    }
    case 'first_trip_not_in_timetables':
      return { code: 'first_trip_not_in_timetables', message, timetables: body.timetables as { id: number; name: string }[] };
    case 'truck_shared_by_trip_groups': {
      const b = body as unknown as { timetable_id: number; truck_id: number; courses: { id: number; code: string; trip_no: number }[] };
      return { code: 'truck_shared_by_trip_groups', message, timetableId: b.timetable_id, truckId: b.truck_id,
        courses: b.courses.map((c) => ({ id: c.id, code: c.code, tripNo: c.trip_no })) };
    }
    case 'linked_trips_exist':
      return { code: 'linked_trips_exist', message };
    case 'route_source_required': {
      const b = body as unknown as { course_id: number; sources: RouteSourceResponse[] };
      return { code: 'route_source_required', message, courseId: b.course_id, sources: b.sources.map(deserializeRouteSource) };
    }
    case 'first_trip_missing': {
      const b = body as unknown as { trips: { course_id: number; code: string; trip_no: number; first_trip_course: { id: number; code: string } }[] };
      return { code: 'first_trip_missing', message, trips: b.trips.map((t) => ({ courseId: t.course_id, code: t.code, tripNo: t.trip_no, firstTripCourse: t.first_trip_course })) };
    }
    case 'place_invalid': {
      const b = body as unknown as { places: { name: string | null; address: string | null; errors: string[] }[] };
      return { code: 'place_invalid', message, places: b.places.map((p) => ({ name: p.name, address: p.address, errors: p.errors })) };
    }
    default:
      return { code: 'other', message, status: error.response?.status ?? null };
  }
}
